import type { QueryRef } from '@apollo/client';
import { useBackgroundQuery, useReadQuery } from '@apollo/client';
import type { FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';

import { generateHTMLArticleContent } from '@xing-com/crate-articles-article-editor';
import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { useParams } from '@xing-com/crate-core-router';
import {
  articles,
  coverLetterArticles,
  metadataMap,
} from '@xing-com/crate-lebenslauf-articles';
import {
  ArticleCard,
  GenericError,
  MetaData,
} from '@xing-com/crate-lebenslauf-components';
import type {
  GetVitaArticleQuery,
  GetVitaArticleQueryVariables,
} from '@xing-com/crate-lebenslauf-graphql';
import { GetVitaArticleDocument } from '@xing-com/crate-lebenslauf-graphql';
import {
  useBasePath,
  useHostName,
  useVitaTracking,
} from '@xing-com/crate-lebenslauf-hooks';
import { NoSSRLoggedInSuspense } from '@xing-com/crate-utils-no-ssr';
import { SeoBreadcrumb } from '@xing-com/seo-breadcrumb';
import { scale360, spaceL } from '@xing-com/tokens';

import { ArticleSkeleton } from './article-skeleton';
import * as Styled from './article.styled';

export const ArticleInner: FC<{
  queryRef: QueryRef<GetVitaArticleQuery, GetVitaArticleQueryVariables>;
}> = ({ queryRef }) => {
  const { formatMessage } = useIntl();
  const hostname = useHostName();
  const basePath = useBasePath();
  const { trackWithNWT } = useVitaTracking({
    pageName: 'articles/article_detailpage',
  });
  const { slug } = useParams();
  const allArticles = [...articles, ...coverLetterArticles];
  const slugOrID =
    allArticles.find((article) => article.url.includes(slug))?.id || slug;
  const { data } = useReadQuery(queryRef);

  const hasBottomScrollTracked = useRef(false);
  const { ref: inViewRef } = useInView({
    onChange: (inView) => {
      if (inView && !hasBottomScrollTracked.current) {
        hasBottomScrollTracked.current = true;
      }
    },
  });

  useEffect(() => {
    if (data) {
      trackWithNWT({
        event: 'article_viewed',
        context: 'anschreiben_acticle_pages_full_text_article_view',
        itemUrn: data?.articlesArticle?.globalId,
        itemActorUrn: data?.articlesArticle?.contextGlobalId,
      });
      trackWithNWT();
    }
  }, [data, trackWithNWT]);

  const articleContent = data?.articlesArticle?.content;
  const htmlContent = useMemo(
    () => generateHTMLArticleContent(articleContent),
    [articleContent]
  );

  const title = data?.articlesArticle?.header?.title?.text;
  const description = data?.articlesArticle?.header?.summary?.text;
  const coverUrl =
    data?.articlesArticle?.header?.cover &&
    'url' in data.articlesArticle.header.cover
      ? data.articlesArticle.header.cover.url
      : null;

  return (
    <Styled.StyledFrame>
      <MetaData
        title={metadataMap[slugOrID]?.title}
        description={metadataMap[slugOrID]?.description}
      />

      <SeoBreadcrumb
        items={[
          {
            title: formatMessage({
              id: 'CRATE_LEBENSLAUF_NAVIGATION_ADVISORY',
            }),
            url: `${basePath}/ratgeber`,
          },
          { title: title || '', url: '' },
        ]}
      />
      <Styled.StyledHero size="small">{title}</Styled.StyledHero>
      <Styled.StyledDescription size="large">
        {description}
      </Styled.StyledDescription>
      {coverUrl && <Styled.StyledCover src={coverUrl} alt="" loading="lazy" />}

      <Styled.StyledArticlesHeadline size="xxlarge">
        <FormattedMessage id="LEBENSLAUF_ARTICLE_CAROUSEL_TITLE" />
      </Styled.StyledArticlesHeadline>
      <Styled.StyledArticlesDescription size="large">
        <FormattedMessage id="LEBENSLAUF_ARTICLE_CAROUSEL_DESCRIPTION" />
      </Styled.StyledArticlesDescription>
      <Styled.StyledCarousel
        itemMaxWidth={scale360}
        itemMinWidth={scale360}
        itemSpacing={spaceL}
        itemSpacingConfined={spaceL}
        itemSnapPosition="center"
      >
        {allArticles
          ?.filter(
            (article) =>
              article.id !== data?.articlesArticle?.id &&
              article.domain &&
              hostname.includes(article.domain)
          )
          .map((article) =>
            article ? (
              <ArticleCard
                key={article.url}
                {...{
                  title: article.title,
                  description: article.description,
                  to: article?.url,
                  image: article.image?.srcWide ?? '',
                  buttonCopy: formatMessage({
                    id: 'CRATE_LEBENSLAUF_STARTPAGE_ARTICLES_READ_MORE',
                  }),
                }}
              />
            ) : null
          )}
      </Styled.StyledCarousel>
      <Styled.EditorContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
      <div ref={inViewRef} />
    </Styled.StyledFrame>
  );
};

export const Article: FC = () => {
  const { slug } = useParams();
  const allArticles = [...articles, ...coverLetterArticles];
  const slugOrID =
    allArticles.find((article) => article.url.includes(slug))?.id || slug;
  const [queryRef, { refetch }] = useBackgroundQuery(GetVitaArticleDocument, {
    variables: {
      slugOrID,
    },
  });

  return (
    <ErrorBoundary onError={() => <GenericError refetch={refetch} />}>
      <NoSSRLoggedInSuspense fallback={<ArticleSkeleton />}>
        <ArticleInner queryRef={queryRef} />
      </NoSSRLoggedInSuspense>
    </ErrorBoundary>
  );
};
